import * as THREE from 'three';

export type ObstacleType = 'ground' | 'flying1' | 'flying2';

export class Obstacle {
  public mesh: THREE.Mesh;
  private speed: number;
  private readonly removeDistance: number = -15; // 画面外に出たら削除
  public readonly isFlying: boolean; // 空を飛んでいるかどうか
  public readonly flyingHeight: number; // 空を飛ぶ障害物の高さ
  private readonly shouldFadeOut: boolean; // 透明化するかどうか
  private elapsedTime: number = 0; // 経過時間
  private readonly fadeOutDelay: number = 0.3; // 透明化開始までの時間（秒）
  private readonly fadeOutDuration: number = 0.2; // 透明化にかける時間（秒）
  private cachedBoundingBox: THREE.Box3; // キャッシュされたバウンディングボックス
  private lastPositionX: number; // 前回のX位置（キャッシュ更新判定用）
  private material: THREE.MeshBasicMaterial; // マテリアル参照のキャッシュ

  constructor(type: ObstacleType, speed: number, shouldFadeOut: boolean = false) {
    this.speed = speed;
    this.shouldFadeOut = shouldFadeOut;
    this.isFlying = type === 'flying1' || type === 'flying2';
    
    // 高さを設定
    if (type === 'flying1') {
      this.flyingHeight = 1.2; // 1段目（地面から約1.2ユニット上）
    } else if (type === 'flying2') {
      this.flyingHeight = 2.5; // 2段目（地面から約2.5ユニット上）
    } else {
      this.flyingHeight = 0;
    }
    
    this.mesh = this.createObstacleMesh();

    // マテリアル参照をキャッシュ
    this.material = this.mesh.material as THREE.MeshBasicMaterial;

    // 障害物の初期位置（画面右側）
    if (this.isFlying) {
      // 空を飛ぶ障害物
      this.mesh.position.set(15, this.flyingHeight, 0);
    } else {
      // 地面の障害物
      this.mesh.position.set(15, 0.5, 0); // 地面の上（0.5 = ブロックの高さ/2）
    }

    // 初期位置でバウンディングボックスを計算
    this.cachedBoundingBox = this.calculateBoundingBox();
    this.lastPositionX = this.mesh.position.x;
  }

  /**
   * 障害物のメッシュを作成（1マスのブロックのみ）
   */
  private createObstacleMesh(): THREE.Mesh {
    // 1マスの立方体のみ
    const geometry = new THREE.BoxGeometry(1, 1, 1);

    // Chrome Dino風のグレー
    const material = new THREE.MeshBasicMaterial({
      color: 0x535353, // グレー
      transparent: this.shouldFadeOut, // 透明化する場合はtransparentを有効化
      opacity: 1.0, // 初期状態は不透明
    });

    const mesh = new THREE.Mesh(geometry, material);
    mesh.castShadow = false;
    mesh.receiveShadow = false;

    return mesh;
  }

  /**
   * 障害物の状態を更新
   */
  public update(deltaTime: number): void {
    // 左に移動
    this.mesh.position.x -= this.speed * deltaTime;

    // X位置が変わったのでバウンディングボックスのキャッシュを更新
    const xDiff = this.mesh.position.x - this.lastPositionX;
    this.cachedBoundingBox.min.x += xDiff;
    this.cachedBoundingBox.max.x += xDiff;
    this.lastPositionX = this.mesh.position.x;

    // 透明化処理
    if (this.shouldFadeOut) {
      this.elapsedTime += deltaTime;

      // 遅延時間経過後、フェード開始
      if (this.elapsedTime >= this.fadeOutDelay) {
        const fadeProgress = (this.elapsedTime - this.fadeOutDelay) / this.fadeOutDuration;

        if (fadeProgress >= 1.0) {
          // フェード完了（キャッシュ済みマテリアルを使用）
          this.material.opacity = 0;
        } else {
          // 徐々に透明化（キャッシュ済みマテリアルを使用）
          this.material.opacity = 1.0 - fadeProgress;
        }
      }
    }
  }

  /**
   * 障害物が画面外に出たかチェック
   */
  public isOutOfScreen(): boolean {
    return this.mesh.position.x < this.removeDistance;
  }

  /**
   * バウンディングボックスを計算（初期化用）
   * BoxGeometryなので手動計算で高速化
   */
  private calculateBoundingBox(): THREE.Box3 {
    // BoxGeometryは1x1x1の立方体なので、位置とスケールから直接計算
    const halfSize = 0.5; // 1x1x1の立方体の半分のサイズ
    const position = this.mesh.position;
    const scale = this.mesh.scale;

    const min = new THREE.Vector3(
      position.x - halfSize * scale.x,
      position.y - halfSize * scale.y,
      position.z - halfSize * scale.z
    );
    const max = new THREE.Vector3(
      position.x + halfSize * scale.x,
      position.y + halfSize * scale.y,
      position.z + halfSize * scale.z
    );

    return new THREE.Box3(min, max);
  }

  /**
   * バウンディングボックスを取得（衝突判定用）
   * キャッシュされたバウンディングボックスを返す（update()で更新済み）
   */
  public getBoundingBox(): THREE.Box3 {
    return this.cachedBoundingBox;
  }

  /**
   * 速度を設定
   */
  public setSpeed(speed: number): void {
    this.speed = speed;
  }

  /**
   * 位置を取得
   */
  public getPosition(): THREE.Vector3 {
    return this.mesh.position.clone();
  }

  /**
   * クリーンアップ
   */
  public dispose(): void {
    this.mesh.geometry.dispose();
    if (Array.isArray(this.mesh.material)) {
      this.mesh.material.forEach(m => m.dispose());
    } else {
      this.mesh.material.dispose();
    }
  }
}
